import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { Users, Globe, Headphones, Heart, Shield, ArrowRight, Trophy, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "/logo.png";

// Gallery images
const galleryImages = [
  { src: '/images/gallery-1.jpg', alt: 'Code Red Sound with Artists' },
  { src: '/images/gallery-2.jpg', alt: 'Code Red Sound with Artists' },
  { src: '/images/gallery-3.jpg', alt: 'Code Red Sound with Artists' },
  { src: '/images/gallery-4.jpg', alt: 'Code Red Sound with Artists' },
  { src: '/images/gallery-5.jpg', alt: 'Code Red Sound with Artists' },
  { src: '/images/gallery-6.jpg', alt: 'Code Red Sound with Artists' },
  { src: '/images/gallery-7.jpg', alt: 'Code Red Sound with Artists' },
  { src: '/images/gallery-8.jpg', alt: 'Code Red Sound with Artists' },
  { src: '/images/gallery-9.jpg', alt: 'Code Red Sound with Artists' },
  { src: '/images/gallery-10.jpg', alt: 'Code Red Sound with Artists' },
];

export default function About() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const { isAuthenticated } = useAuth();

  const openLightbox = (index) => {
    setCurrentImage(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const values = [
    {
      icon: Heart,
      title: "Passion for Music",
      description: "We live and breathe Caribbean music. Every dubplate on our platform represents authentic culture and artistry."
    },
    {
      icon: Users,
      title: "Artist First",
      description: "We're committed to fair compensation and giving artists direct access to their fans worldwide."
    },
    {
      icon: Shield,
      title: "Trust & Security",
      description: "Secure payments, verified artists, and a satisfaction guarantee protect both buyers and sellers."
    },
    {
      icon: Globe,
      title: "Global Reach",
      description: "Connecting Caribbean artists with fans across the globe, breaking down geographical barriers."
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="hero-gradient absolute inset-0 pointer-events-none" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <img 
              src={LOGO_URL} 
              alt="Tune4Tune" 
              className="h-24 w-auto mx-auto mb-6"
            />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              About <span className="text-lime">Tune4Tune</span>
            </h1>
            <p className="text-lg sm:text-xl text-[#A1A1AA] max-w-2xl mx-auto">
              Bridging the gap between Caribbean music legends and fans worldwide through 
              personalized dubplates and custom voiceovers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 bg-surface/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={fadeInUp.initial}
            whileInView={fadeInUp.animate}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">Our Story</h2>
          </motion.div>

          <motion.div
            initial={fadeInUp.initial}
            whileInView={fadeInUp.animate}
            viewport={{ once: true }}
          >
            <div className="bg-surface border border-[#262626] rounded-2xl p-8 sm:p-12 space-y-8">
              <p className="text-lg text-[#A1A1AA] leading-relaxed">
                <span className="text-lime font-semibold">Tune4Tune</span> was born from a simple idea: 
                give fans direct access to their favorite Caribbean artists for personalized content. 
                What started as a passion project has grown into the premier destination for authentic 
                dancehall, reggae and soca dubplates.
              </p>

              {/* Who Are We Section */}
              <div className="pt-6 border-t border-[#262626]">
                <h3 className="font-heading text-2xl font-bold mb-4 flex items-center gap-3">
                  <Trophy className="w-6 h-6 text-lime" />
                  Who are we?
                </h3>
                <p className="text-lg text-[#A1A1AA] leading-relaxed">
                  <span className="text-lime font-semibold">Tune4Tune</span> was created by{' '}
                  <span className="text-lime font-semibold">Code Red Sound</span>, a Caribbean sound system 
                  rooted in dancehall culture, sound clash tradition, and global club performance. Led by{' '}
                  <span className="text-white font-medium">CodeLank</span> and{' '}
                  <span className="text-white font-medium">Chris Dymond</span>, the sound is known for sharp 
                  selection, dubplate-driven strategy, and commanding crowd control.
                </p>
                <p className="text-lg text-[#A1A1AA] leading-relaxed mt-4">
                  The current reigning <span className="text-lime font-semibold">World Clash Champion</span>, 
                  Code Red Sound represents Caribbean excellence on international stages, seamlessly blending 
                  dancehall, reggae, soca, hip-hop, afrobeats, and open-format sounds while staying true to its roots.
                </p>
              </div>
              
              <p className="text-lg text-[#A1A1AA] leading-relaxed pt-6 border-t border-[#262626]">
                We work directly with verified artists to ensure authenticity, fair pricing, and 
                timely delivery. Whether you're a DJ looking for exclusive drops, a radio host 
                needing personalized intros, or a fan wanting a birthday shoutout from your 
                favorite artist — we've got you covered.
              </p>

              <div className="pt-6 border-t border-[#262626]">
                <p className="text-center">
                  <span className="text-[#666]">Created by</span>{' '}
                  <span className="text-lime font-heading font-bold text-xl">Code Red Sound</span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={fadeInUp.initial}
            whileInView={fadeInUp.animate}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-2">Code Red Sound in Action</h2>
            <p className="text-[#A1A1AA]">World Clash Champions with various artists</p>
          </motion.div>

          {/* Masonry-style Gallery Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 gap-3 space-y-3"
          >
            {galleryImages.map((image, index) => (
              <motion.div
                key={image.alt}
                variants={itemVariants}
                className="break-inside-avoid"
              >
                <div 
                  className="relative group cursor-pointer overflow-hidden rounded-xl"
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-12 h-12 bg-lime/90 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Previous button */}
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-lime/80 rounded-full flex items-center justify-center transition-colors group z-10"
            >
              <ChevronLeft className="w-6 h-6 group-hover:text-black" />
            </button>

            {/* Next button */}
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-lime/80 rounded-full flex items-center justify-center transition-colors group z-10"
            >
              <ChevronRight className="w-6 h-6 group-hover:text-black" />
            </button>

            {/* Image */}
            <motion.img
              key={currentImage}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              src={galleryImages[currentImage].src}
              alt={galleryImages[currentImage].alt}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Image counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 px-4 py-2 rounded-full text-sm">
              {currentImage + 1} / {galleryImages.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Values Section */}
      <section className="py-16 bg-surface/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={fadeInUp.initial}
            whileInView={fadeInUp.animate}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">Our Values</h2>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Everything we do is guided by these core principles
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {values.map((value) => (
              <motion.div
                key={value.title}
                variants={itemVariants}
                className="bg-surface border border-[#262626] rounded-xl p-6 hover:border-lime/50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-lime/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <value.icon className="w-6 h-6 text-lime" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg mb-2">{value.title}</h3>
                    <p className="text-[#A1A1AA] text-sm">{value.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={fadeInUp.initial}
            whileInView={fadeInUp.animate}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">How It Works</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Choose Your Artist", desc: "Browse our roster of verified Caribbean artists and select your favorite" },
              { step: "2", title: "Send Your Message", desc: "Tell the artist what you want — drops, shoutouts, custom lyrics" },
              { step: "3", title: "Receive Your Dubplate", desc: "Get your personalized content delivered within the promised timeframe" }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={fadeInUp.initial}
                whileInView={fadeInUp.animate}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-lime rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-black font-heading font-bold text-2xl">{item.step}</span>
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-[#A1A1AA] text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-surface/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={fadeInUp.initial}
            whileInView={fadeInUp.animate}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-lime/20 to-lime/5 border border-lime/30 rounded-2xl p-8 sm:p-12 text-center"
          >
            <Headphones className="w-16 h-16 text-lime mx-auto mb-6" />
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Ready to Get Your Dubplate?
            </h2>
            <p className="text-[#A1A1AA] mb-8 max-w-xl mx-auto">
              Join thousands of DJs, radio hosts, and music lovers who've gotten exclusive 
              content from their favorite artists.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/artists">
                <Button className="btn-primary">
                  Browse Artists
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              {!isAuthenticated && (
                <Link to="/register">
                  <Button variant="outline" className="border-lime text-lime hover:bg-lime hover:text-black">
                    Create Account
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <div className="flex-grow" />
      <Footer />
    </div>
  );
}
